/**
 * Mapping route → section de navigation (P2 garde de route, 2026-07-07).
 *
 * Les ids de section sont ceux de ROLE_SECTIONS (lib/auth.tsx) et du rail de
 * navigation (components/layout/app-shell.tsx). La garde de route client
 * (components/layout/section-guard.tsx) s'appuie sur ce mapping : si
 * l'utilisateur n'a pas la section (hasSection), la page rend un 403 honnête
 * au lieu du contenu.
 *
 * Périmètre volontairement conservateur :
 *  - correspondance par préfixe de segment (le préfixe le plus long gagne),
 *    donc « /admin/payment/* » (paiements) prime sur « /admin » (admin) ;
 *  - les routes non mappées (« / », /sedec/**, pages de détail inconnues)
 *    renvoient null → non gardées ;
 *  - « contenu-diocese » (actualités/newsletter) est mappé mais HORS de
 *    ALL_SECTIONS : aucune section de rôle ne le contient, la garde ne
 *    l'oppose donc à personne (comportement historique conservé).
 */

const SECTION_BY_PREFIX: Record<string, string> = {
  // Flux financiers (02/11/2026) : accueil de l'Économat diocésain.
  "/flux": "essentiel",
  // Direction Générale (GÉNÉRA)
  "/dashboard": "general",
  "/observatoire": "general",
  "/analytics": "general",
  "/decisions": "general",
  "/conformite": "general",
  "/benchmarking": "general",
  "/cartographie": "general",
  "/rapports": "general",
  "/tableau-personnalise": "general",
  "/admin/dashboard-diocese": "general",
  // Suivi des dons (20/10/2026) et son écran de paramétrage des accès
  // (/suivi-dons/acces). La page elle-même est aussi ouverte, hors section,
  // à tout rôle admis par donation-service (section-guard.tsx).
  "/suivi-dons": "general",
  "/rapprochement-jeko": "essentiel",

  // Cabinet
  "/mon-compte": "cabinet",
  "/notifications": "cabinet",
  "/messagerie": "cabinet",
  "/support": "cabinet",
  "/aide": "cabinet",
  "/calendrier": "cabinet",

  // Finances (FINANC)
  "/tresorerie": "finances",
  "/journal-transactions": "finances",
  "/transactions": "finances",
  "/reconciliation": "finances",
  "/reversements": "finances",
  "/caisse-versements": "finances",
  "/parametrage-comptable": "finances",
  "/cheques": "finances",
  "/comptabilite": "finances",
  "/budget": "finances",
  "/rentabilite-directions": "finances",

  // Péréquation (PÉRÉQU)
  "/perequation": "perequation",
  "/cloture-hebdo": "perequation",
  "/denier-culte": "perequation",
  "/intentions-messe": "perequation",
  "/quetes-speciales": "perequation",
  "/fds": "perequation",
  "/dons-nature": "perequation",
  "/collectes": "perequation",
  "/admin/collectes": "perequation",
  "/admin/contributions": "perequation",

  // Procure / Centrale d'achats
  "/fournisseurs": "procure",
  "/factures-achats": "procure",
  "/gestion-dette": "procure",

  // Projets & patrimoine
  "/projets": "projets",
  "/patrimoine": "projets",
  "/admin/solidarite-projets": "projets",

  // Séminaires & institutions
  "/formation-diocesaine": "seminaires",
  "/liturgie": "seminaires",
  "/sacrements": "seminaires",
  "/admin/pelerinages": "seminaires",

  // Paroisses & clergé
  "/paroisses": "paroisses",
  "/sante-paroisses": "paroisses",
  "/visites-pastorales": "paroisses",
  "/clerge": "paroisses",

  // SEDEC
  "/sedec": "sedec",

  // Communication & médias
  "/communication": "communication",
  "/catho-app": "communication",
  "/catho-media": "communication",
  "/admin/actualites": "communication",
  "/admin/newsletter": "communication",
  "/admin/calendrier-editorial": "communication",
  "/admin/evenements": "communication",
  "/admin/annonces": "communication",
  "/admin/documents": "communication",
  "/admin/radio-programmes": "communication",
  "/admin/semaine-diocesaine": "communication",

  // Administration & systèmes (paiements PSP inclus)
  "/admin": "admin",
  "/integrations": "admin",
};

const PREFIXES = Object.keys(SECTION_BY_PREFIX).sort((a, b) => b.length - a.length);

/**
 * Section de navigation correspondant à un chemin, ou null si le chemin
 * n'est rattaché à aucune section (login, SEDEC, 404, …).
 */
export function sectionForPath(path: string): string | null {
  for (const prefix of PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return SECTION_BY_PREFIX[prefix];
    }
  }
  return null;
}
